/**
 * Manage Users — every account, in the two shapes it can take.
 *
 * Two tabs: Club Staff (club-side accounts, listed with their club
 * memberships) and Priori Staff (our people). Both are fed by the same
 * endpoint with one negated predicate, so nobody appears in both tabs or
 * in neither. Adding a user goes to POST /api/v1/admin/users; a created
 * account is passwordless on purpose — sign-in is federated.
 *
 * PRIORI PEOPLE ONLY. A club's members are managed on the club, in Manage
 * Clubs. The screen this replaced listed every account in the system with its
 * club memberships, which put the same person in two places and made "who can
 * reach this club" a question you answered by scrolling.
 *
 * CLUB MEMBERSHIPS ARE STILL SHOWN HERE, in red, when a Priori address is
 * inside a club. They should not be: staff reach every club through platform
 * scope, so the row grants nothing and only makes the club's own member list
 * unreadable. Migration 035 removes them; until it runs, this is where you
 * can see what is left.
 *
 * EVERY CHANGE NEEDS A REASON — the server refuses the write without one, so
 * the box is the widest control on the panel rather than an optional footnote.
 */
import { Eye, Search, UserPlus, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api, type Session } from '../lib/api'
import { setViewAsGrant } from '../lib/viewAs'
import './admin.css'
import './insights-lab.css'  // lab-tabs, as on Manage insights

type Membership = {
  id: number; code: string; name: string; role: string | null; stored_role: string
}
type StaffUser = {
  id: number; email: string
  is_active: boolean
  is_platform_admin: boolean
  is_priori_staff: boolean
  can_impersonate: boolean
  last_login_at: string | null
  created_at: string | null
  organizations: Membership[]
}
type Payload = { domain: string; total: number; roles: string[]; users: StaffUser[] }

const FLAGS = [
  { key: 'is_platform_admin', label: 'Platform admin',
    hint: 'Every club, plus the platform screens that change who can reach what.' },
  { key: 'is_priori_staff', label: 'Priori staff',
    hint: 'Read every club. Writing inside one still needs a membership row.' },
  { key: 'can_impersonate', label: 'May impersonate',
    hint: 'View the product as another person, read-only. Never as a platform admin.' },
  // 'qa_enabled' left this list 2026-09-23: staff are qa-enabled by rule
  // (authz.py), so the toggle audited revocations that revoked nothing.
  { key: 'is_active', label: 'Active',
    hint: 'Turning this off stops the account signing in at all.' },
] as const
type FlagKey = (typeof FLAGS)[number]['key']

const when = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : 'never')

function StaffTab({ session }: { session: Session }) {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(() => {
    api<Payload>('/api/v1/admin/users')
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(load, [load])

  const shown = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    return q ? data.users.filter((u) => u.email.toLowerCase().includes(q)) : data.users
  }, [data, query])

  if (error) {
    return <p className="error">{error}</p>
  }
  if (!data) return <p className="muted">Loading…</p>

  const admins = data.users.filter((u) => u.is_platform_admin).length
  const inClubs = data.users.filter((u) => u.organizations.length > 0)
  const strayRows = inClubs.reduce((n, u) => n + u.organizations.length, 0)
  /* A viewer membership really does grant nothing beyond the read staff hold
     everywhere; analyst and admin grant WRITE on that club. authz.can() gives
     staff implicit READ only, and anything above it needs a membership row —
     so the two cases are not the same finding and this screen used to call
     them both harmless. */
  const writeRows = inClubs.reduce(
    (n, u) => n + u.organizations.filter((o) => o.role !== 'viewer').length, 0)

  return (
    <>
      <p className="page-sub">
        {data.total} Priori {data.total === 1 ? 'person' : 'people'} — {admins} platform
        admin. Club members are managed on their club, in Manage Clubs.
      </p>
      {/* The IMPERSONATE chip below says a person MAY impersonate; it is not a
          button. Impersonation starts from the person you want to become, and
          they are listed on their club — so the control lives there, and this
          page says where rather than leaving you to find it. */}
      <p className="page-sub">
        To view Priori as someone else, open their club in{' '}
        <Link to="/manage-clubs">Manage Clubs</Link> and use <strong>View as</strong>{' '}
        beside their name. A yellow banner names who you are acting as until you
        leave, and the audit log records both identities.
      </p>

      {strayRows > 0 && (
        <div className="panel adm-warn">
          <strong>{strayRows} club membership{strayRows === 1 ? '' : 's'}</strong> still
          sit against {inClubs.length} Priori {inClubs.length === 1 ? 'account' : 'accounts'}.
          {writeRows > 0 && (
            <>
              {' '}
              <strong>{writeRows} of them grant WRITE</strong> on that club —
              staff read every club already, but analyst and admin memberships
              add the ability to change things, which is access nobody granted
              deliberately.
            </>
          )}{' '}
          They also make each club’s own member list unreadable. Migration{' '}
          <code>035</code> removes them.
        </div>
      )}

      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Find</span>
          <input className="adm-input" style={{ minWidth: 280 }}
                 placeholder={`name@${data.domain}`}
                 value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          <Search size={12} style={{ verticalAlign: -2, marginRight: 6 }} />
          {shown.length} shown
        </span>
      </div>

      <AddUserPanel mode="staff" onAdded={load} />

      <table className="adm-table">
        <thead>
          <tr><th>Person</th><th>Access</th><th>Extra club access</th><th>Last sign-in</th><th /></tr>
        </thead>
        <tbody>
          {shown.map((u) => (
            <StaffRow key={u.id} user={u} open={openId === u.id}
                      canImpersonate={session.may_impersonate}
                      onToggle={() => setOpenId(openId === u.id ? null : u.id)}
                      onChanged={load} />
          ))}
          {shown.length === 0 && (
            <tr><td colSpan={5} className="adm-empty">No account matches “{query}”.</td></tr>
          )}
        </tbody>
      </table>
    </>
  )
}

/** Start impersonating, then land on the app root. A full load, not a router
 *  navigation: every mounted page was rendered as the real person and none of
 *  them know to refetch. */
async function viewAs(user: StaffUser) {
  try {
    // this tab's grant: only this tab views as them (lib/viewAs)
    const r = await api<{ view_as: string }>(`/api/v1/admin/impersonate/${user.id}`, {
      method: 'POST',
      body: JSON.stringify({ reason: `support: viewing ${user.email}` }),
    })
    setViewAsGrant(r.view_as)
  } catch (e) {
    // Most likely already_impersonating (409): starting a second one is
    // refused server-side — no silent failure, say why.
    window.alert(e instanceof ApiError ? e.message
      : 'Could not start viewing as this person.')
    return
  }
  window.location.assign('/')
}

function StaffRow({ user, open, canImpersonate, onToggle, onChanged }: {
  canImpersonate: boolean
  user: StaffUser; open: boolean; onToggle: () => void; onChanged: () => void
}) {
  return (
    <>
      <tr className={`is-clickable${open ? ' is-open' : ''}`} tabIndex={0} role="button"
          aria-expanded={open} onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() }
          }}>
        <td className="adm-primary">{user.email}</td>
        <td>
          <div className="adm-chips">
            {user.is_platform_admin && <span className="adm-chip adm-chip--admin">PLATFORM ADMIN</span>}
            {user.is_priori_staff && <span className="adm-chip adm-chip--staff">STAFF</span>}
            {user.can_impersonate && <span className="adm-chip adm-chip--impersonate">IMPERSONATE</span>}
            {!user.is_active && <span className="adm-chip adm-chip--off">INACTIVE</span>}
          </div>
        </td>
        <td>
          {user.organizations.length === 0
            ? <span className="muted">none — reads every club as staff</span>
            : (
              <div className="adm-chips">
                {/* The ROLE is the point, not the club: viewer adds nothing to
                    what staff already hold, analyst and admin add write. */}
                {user.organizations.map((o) => (
                  <span
                    key={o.id}
                    className={`adm-chip adm-chip--${o.role === 'viewer' ? 'staff' : 'admin'}`}
                    title={o.role === 'viewer'
                      ? `${o.code}: viewer — no more than staff read`
                      : `${o.code}: ${o.role} — can write to this club`}
                  >
                    {o.code} · {o.role}
                  </span>
                ))}
              </div>
            )}
        </td>
        <td className="adm-when">{when(user.last_login_at)}</td>
        <td className="adm-num" style={{ whiteSpace: 'nowrap' }}>
          {/* Priori staff belong to no club, so they appear in no club's
              member list — which is where "View as" otherwise lives. Without
              this there is no way to reach them at all.

              Not offered for platform admins: nobody impersonates one, not
              even another admin (authz.can_impersonate, tightened
              2026-08-29). The server refuses it regardless; hiding the button
              means the refusal is not something you have to discover. */}
          {canImpersonate && user.is_active && !user.is_platform_admin && (
            <button className="adm-btn adm-btn--quiet"
                    title={`See Priori exactly as ${user.email} sees it`}
                    onClick={(e) => { e.stopPropagation(); void viewAs(user) }}>
              <Eye size={12} /> View as
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="adm-editor">
          <td colSpan={4}>
            <StaffEditor user={user} onClose={onToggle} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  )
}

function StaffEditor({ user, onClose, onChanged }: {
  user: StaffUser; onClose: () => void; onChanged: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const setFlag = async (key: FlagKey, value: boolean) => {
    setBusy(true); setMsg(null)
    try {
      await api(`/api/v1/admin/users/${user.id}`, {
        method: 'PATCH', body: JSON.stringify({ [key]: value, reason }),
      })
      setMsg({ ok: true, text: `${key.replace(/_/g, ' ')} is now ${value ? 'on' : 'off'}.` })
      setReason(''); onChanged()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  const revoke = async (orgId: number) => {
    setBusy(true); setMsg(null)
    try {
      await api(`/api/v1/admin/users/${user.id}/memberships/${orgId}`, {
        method: 'DELETE', body: JSON.stringify({ reason }),
      })
      setMsg({ ok: true, text: 'Membership removed.' }); setReason(''); onChanged()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-editor__inner">
      <h3>{user.email}</h3>
      <p className="adm-editor__hint">
        Account created {when(user.created_at)}. Changes take effect on this
        person’s next request; they are not signed out.
      </p>

      {/* Same split as Manage Clubs: Close sat on top of the reason input
          whenever the panel was wide enough for the input to grow under it. */}
      <div className="adm-row">
        <label className="adm-field adm-reason">
          <span className="t-label">Reason — required</span>
          <input className="adm-input" placeholder="e.g. joined the platform team, ticket OPS-142"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      <div className="adm-actions">
        <button className="adm-btn adm-btn--quiet" onClick={onClose}>
          <X size={13} /> Close
        </button>
      </div>

      <div className="adm-toggles">
        {FLAGS.map((f) => (
          <label key={f.key} className={`adm-toggle${user[f.key] ? ' is-on' : ''}`} title={f.hint}>
            <input type="checkbox" checked={user[f.key]}
                   disabled={busy || !reason.trim()}
                   onChange={(e) => void setFlag(f.key, e.target.checked)} />
            {f.label}
          </label>
        ))}
      </div>

      <SetPassword user={user} reason={reason} onDone={() => setReason('')} />

      {user.organizations.length > 0 && (
        <>
          <p className="adm-editor__hint">
            Club memberships that should not exist. Removing one costs this
            person nothing — their staff or platform access already reaches
            every club.
          </p>
          <div className="adm-row">
            {user.organizations.map((o) => (
              <button key={o.id} className="adm-btn adm-btn--danger"
                      disabled={busy || !reason.trim()}
                      onClick={() => void revoke(o.id)}>
                Remove from {o.code}
              </button>
            ))}
          </div>
        </>
      )}

      {!reason.trim() && <p className="adm-msg muted">Give a reason to enable the controls.</p>}
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </div>
  )
}

/* ── Add user (both tabs) ──────────────────────────────────────────────── */

type OrgOption = { id: number; code: string; name: string }

function AddUserPanel({ mode, onAdded }: {
  mode: 'club' | 'staff'; onAdded: () => void
}) {
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')
  const [orgId, setOrgId] = useState('')
  const [role, setRole] = useState('')
  const [orgs, setOrgs] = useState<OrgOption[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (mode !== 'club') return
    api<{ organizations: OrgOption[]; roles: string[] }>('/api/v1/admin/orgs')
      .then((d) => {
        setOrgs(d.organizations); setRoles(d.roles)
        if (d.roles[0]) setRole((r) => r || d.roles[0]!)
      })
      .catch(() => { /* submitting will name the failure */ })
  }, [mode])

  const ready = email.trim().includes('@') && reason.trim() !== ''
    && (mode === 'staff' || orgId !== '')

  const submit = async () => {
    setBusy(true); setMsg(null)
    try {
      const body = mode === 'staff'
        ? { email: email.trim(), staff: true, reason }
        : { email: email.trim(), org_id: Number(orgId), role, reason }
      const d = await api<{ created: boolean; email: string }>(
        '/api/v1/admin/users',
        { method: 'POST', body: JSON.stringify(body) })
      setMsg({
        ok: true,
        text: d.created
          ? `${d.email} created and added. They sign in with Google or Microsoft.`
          : `${d.email} added.`,
      })
      setEmail(''); setReason('')
      onAdded()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  return (
    <div className="panel adm-add">
      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Email</span>
          <input className="adm-input" style={{ minWidth: 240 }}
                 placeholder="name@club.com" value={email}
                 onChange={(e) => setEmail(e.target.value)} />
        </label>
        {mode === 'club' && (
          <>
            <label className="adm-field">
              <span className="t-label">Club</span>
              <select className="adm-select" value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}>
                <option value="">Choose a club…</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name} ({o.code})</option>
                ))}
              </select>
            </label>
            <label className="adm-field">
              <span className="t-label">Role</span>
              <select className="adm-select" value={role}
                      onChange={(e) => setRole(e.target.value)}>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          </>
        )}
        <label className="adm-field adm-reason">
          <span className="t-label">Reason required</span>
          <input className="adm-input" value={reason}
                 placeholder="e.g. new analyst at the club, ticket OPS-142"
                 onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      {/* Own row, same cure as the edit panels (see admin.css .adm-actions):
          sharing the reason's row is how the button rendered on top of it. */}
      <div className="adm-actions">
        <button className="adm-btn" disabled={busy || !ready}
                onClick={() => void submit()}>
          <UserPlus size={13} />
          {mode === 'staff' ? 'Add to Priori Staff' : 'Add to Club Staff'}
        </button>
      </div>
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </div>
  )
}

/* ── Club Staff tab ────────────────────────────────────────────────────── */

function ClubTab() {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [club, setClub] = useState('') // org code; '' = every club
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(() => {
    api<Payload>('/api/v1/admin/users?scope=club')
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(load, [load])

  // Built from the accounts themselves, not /admin/orgs: the filter offers
  // exactly the clubs that appear in the table, so choosing one can never
  // produce an empty list for a club that simply has no accounts.
  const clubs = useMemo(() => {
    const seen = new Map<string, string>()
    for (const u of data?.users ?? [])
      for (const o of u.organizations) if (!seen.has(o.code)) seen.set(o.code, o.name)
    return [...seen.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  const shown = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    let users = data.users
    if (club) users = users.filter((u) => u.organizations.some((o) => o.code === club))
    if (q) users = users.filter((u) => u.email.toLowerCase().includes(q))
    return users
  }, [data, query, club])

  if (error) return <p className="error">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  return (
    <>
      <p className="page-sub">
        {data.total} club {data.total === 1 ? 'account' : 'accounts'}. Roles and
        memberships are managed on the club, in{' '}
        <Link to="/manage-clubs">Manage Clubs</Link>.
      </p>

      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Find</span>
          <input className="adm-input" style={{ minWidth: 280 }}
                 placeholder="Search by email"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">Club</span>
          <select className="adm-select" value={club}
                  onChange={(e) => setClub(e.target.value)}>
            <option value="">All clubs</option>
            {clubs.map((c) => (
              <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
            ))}
          </select>
        </label>
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          <Search size={12} style={{ verticalAlign: -2, marginRight: 6 }} />
          {shown.length}
        </span>
      </div>

      <AddUserPanel mode="club" onAdded={load} />

      <table className="adm-table">
        <thead>
          <tr><th>Person</th><th>Clubs</th><th>Status</th><th>Last sign-in</th></tr>
        </thead>
        <tbody>
          {shown.map((u) => (
            <ClubRow key={u.id} user={u} open={openId === u.id}
                     onToggle={() => setOpenId(openId === u.id ? null : u.id)}
                     onChanged={load} />
          ))}
          {shown.length === 0 && (
            <tr><td colSpan={4} className="adm-empty">No account matches the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </>
  )
}

function ClubRow({ user, open, onToggle, onChanged }: {
  user: StaffUser; open: boolean; onToggle: () => void; onChanged: () => void
}) {
  return (
    <>
      <tr className={`is-clickable${open ? ' is-open' : ''}`} tabIndex={0} role="button"
          aria-expanded={open} onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() }
          }}>
        <td className="adm-primary">{user.email}</td>
        <td>
          {user.organizations.length === 0
            ? <span className="muted">no club</span>
            : (
              <div className="adm-chips">
                {user.organizations.map((o) => (
                  <span key={o.id} className="adm-chip" title={`${o.name}`}>
                    {o.code} {o.role ?? o.stored_role}
                  </span>
                ))}
              </div>
            )}
        </td>
        <td>
          {user.is_active
            ? <span className="adm-chip">ACTIVE</span>
            : <span className="adm-chip adm-chip--off">DISABLED</span>}
        </td>
        <td className="adm-when">{when(user.last_login_at)}</td>
      </tr>
      {open && (
        <tr className="adm-editor">
          <td colSpan={4}>
            <ClubEditor user={user} onClose={onToggle} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  )
}

function ClubEditor({ user, onClose, onChanged }: {
  user: StaffUser; onClose: () => void; onChanged: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const setActive = async (value: boolean) => {
    setBusy(true); setMsg(null)
    try {
      await api(`/api/v1/admin/users/${user.id}`, {
        method: 'PATCH', body: JSON.stringify({ is_active: value, reason }),
      })
      setMsg({ ok: true, text: value ? 'Account re-enabled.' : 'Account disabled.' })
      setReason(''); onChanged()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-editor__inner">
      <h3>{user.email}</h3>
      <p className="adm-editor__hint">
        Created {when(user.created_at)}. Disabling stops this account signing
        in at all; it does not remove club memberships.
      </p>

      <div className="adm-row">
        <label className="adm-field adm-reason">
          <span className="t-label">Reason required</span>
          <input className="adm-input"
                 placeholder="e.g. left the club, ticket OPS-142"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>

      <div className="adm-actions">
        {user.is_active
          ? <button className="adm-btn adm-btn--danger"
                    disabled={busy || !reason.trim()}
                    onClick={() => void setActive(false)}>
              Disable account
            </button>
          : <button className="adm-btn"
                    disabled={busy || !reason.trim()}
                    onClick={() => void setActive(true)}>
              Re-enable account
            </button>}
        <button className="adm-btn adm-btn--quiet" onClick={onClose}>
          <X size={13} /> Close
        </button>
      </div>

      <SetPassword user={user} reason={reason} onDone={() => setReason('')} />

      {!reason.trim() && <p className="adm-msg muted">Give a reason to enable the controls.</p>}
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </div>
  )
}

/* ── Set a password (both editors) ─────────────────────────────────────── */

/** Mirrors passwords.MIN_LENGTH on the server, which decides. */
const MIN_PASSWORD_LENGTH = 12

/** An admin chooses this account's password — for a club user who signs in
 *  with email and password rather than Google or Microsoft. Uses the
 *  editor's reason; the value itself is never logged or shown again. */
function SetPassword({ user, reason, onDone }: {
  user: StaffUser; reason: string; onDone: () => void
}) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const save = async () => {
    setBusy(true); setMsg(null)
    try {
      await api(`/api/v1/admin/users/${user.id}/password`, {
        method: 'POST', body: JSON.stringify({ password, reason }),
      })
      setPassword('')
      setMsg({ ok: true, text: `Password set. ${user.email} can sign in with it now, and change it from “Forgot password”.` })
      onDone()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  const short = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  return (
    <>
      <div className="adm-row">
        <label className="adm-field">
          <span className="t-label">Set password</span>
          <input className="adm-input" type="password" autoComplete="new-password"
                 style={{ minWidth: 240 }}
                 placeholder={`at least ${MIN_PASSWORD_LENGTH} characters`}
                 value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <button className="adm-btn"
                disabled={busy || !reason.trim() || password.length < MIN_PASSWORD_LENGTH}
                onClick={() => void save()}>
          Set password
        </button>
      </div>
      {short && <p className="adm-msg muted">At least {MIN_PASSWORD_LENGTH} characters.</p>}
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </>
  )
}

/* ── The page: two tabs ────────────────────────────────────────────────── */

export default function ManageUsers({ session, tab }: {
  session: Session; tab: 'club' | 'staff'
}) {
  return (
    <>
      <h1 className="page-title">Manage Users</h1>
      <nav className="lab-tabs" aria-label="Manage users sections">
        <Link to="/manage-users" className={tab === 'club' ? 'is-on' : ''}
              aria-current={tab === 'club' ? 'page' : undefined}>
          Club Staff
        </Link>
        <Link to="/manage-users/staff" className={tab === 'staff' ? 'is-on' : ''}
              aria-current={tab === 'staff' ? 'page' : undefined}>
          Priori Staff
        </Link>
      </nav>
      {tab === 'club' ? <ClubTab /> : <StaffTab session={session} />}
    </>
  )
}
